/**
 * Parametric city icon — v0M2.4a.
 *
 * Replaces the v0M2.4 filled dot with a walled-circle drawing:
 *   - outer wall ring (empire-tinted stroke)
 *   - inner keep circle
 *   - 3 tower pips at 12, 4, 8 o'clock
 *   - drop shadow for lift off the terrain
 *
 * Screen-scaled per MAP.md §1. Base radius grows subtly with zoom step
 * so icons stay proportional to the visible cells.
 */

import type { Camera } from './camera';
import { pxPerCell } from './camera';

interface CityIconOptions {
  x: number;
  y: number;
  wallColor: string;
  keepColor: string;
  radius: number;
  /**
   * v0M12.4 (2026-07-26) — Optional walled flag. When true, adds a
   * crenellated outer ring around the wall circle so the player can
   * tell at a glance which settlements have the `Settlement Walls`
   * structure built. Undefined/false = current keep-and-towers look.
   */
  walled?: boolean;
}

const KEEP_COLOR = '#c9a24e';
const WALL_STROKE = '#1a140a';
const SHADOW = 'rgba(0, 0, 0, 0.35)';

export function cityIconRadius(camera: Camera): number {
  const z = pxPerCell(camera);
  // Slightly larger at higher zoom, but bounded.
  return Math.max(6, Math.min(11, 6 + z * 0.35));
}

export function drawCityIcon(ctx: CanvasRenderingContext2D, opts: CityIconOptions): void {
  const { x, y, radius, wallColor, keepColor, walled } = opts;

  // Drop shadow
  ctx.save();
  ctx.fillStyle = SHADOW;
  ctx.beginPath();
  ctx.arc(x + 1, y + 2, radius, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();

  // Outer wall
  ctx.save();
  ctx.fillStyle = wallColor;
  ctx.strokeStyle = WALL_STROKE;
  ctx.lineWidth = 1.25;
  ctx.beginPath();
  ctx.arc(x, y, radius, 0, Math.PI * 2);
  ctx.fill();
  ctx.stroke();
  ctx.restore();

  // Inner keep
  ctx.save();
  ctx.fillStyle = keepColor;
  ctx.strokeStyle = WALL_STROKE;
  ctx.lineWidth = 0.75;
  ctx.beginPath();
  ctx.arc(x, y, Math.max(2, radius * 0.45), 0, Math.PI * 2);
  ctx.fill();
  ctx.stroke();
  ctx.restore();

  // Tower pips at 12 / 4 / 8 o'clock
  ctx.save();
  ctx.fillStyle = wallColor;
  ctx.strokeStyle = WALL_STROKE;
  ctx.lineWidth = 0.75;
  const pipR = Math.max(1, radius * 0.28);
  for (const ang of [-Math.PI / 2, Math.PI / 6, (5 * Math.PI) / 6]) {
    const px = x + Math.cos(ang) * radius * 0.9;
    const py = y + Math.sin(ang) * radius * 0.9;
    ctx.beginPath();
    ctx.arc(px, py, pipR, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
  }
  ctx.restore();

  // v0M12.4 (2026-07-26) — walled marker: crenellated outer ring
  // outside the wall circle. Drawn LAST so the crenellations render
  // on top of the base icon. Ring radius extends `radius * 1.35`;
  // 8 crenellation caps evenly spaced.
  if (walled) {
    ctx.save();
    ctx.strokeStyle = WALL_STROKE;
    ctx.lineWidth = 1;
    ctx.fillStyle = wallColor;
    const ringR = radius * 1.35;
    const capR = Math.max(1.5, radius * 0.28);
    const CAPS = 8;
    for (let i = 0; i < CAPS; i++) {
      const ang = (i / CAPS) * Math.PI * 2 - Math.PI / 2;
      const cx = x + Math.cos(ang) * ringR;
      const cy = y + Math.sin(ang) * ringR;
      // Crenellation cap = small rectangle oriented outward from the
      // wall centre. Approximated as a rotated square draw via rect.
      ctx.save();
      ctx.translate(cx, cy);
      ctx.rotate(ang + Math.PI / 2);
      ctx.beginPath();
      ctx.rect(-capR, -capR * 0.7, capR * 2, capR * 1.4);
      ctx.fill();
      ctx.stroke();
      ctx.restore();
    }
    ctx.restore();
  }

  // Unused param defensively referenced so noUnusedParameters is happy
  void KEEP_COLOR;
}

/** Format a rgb-tuple as a CSS rgb() string. */
export function rgbToCss(rgb: readonly [number, number, number], alpha?: number): string {
  return alpha === undefined
    ? `rgb(${rgb[0]}, ${rgb[1]}, ${rgb[2]})`
    : `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${alpha})`;
}
